import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles/createTheme";
import Menu01Icon from "@untitled-ui/icons-react/build/esm/Menu01";
import PropTypes from "prop-types";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { RouterLink } from "src/components/router-link";
import { usePathname } from "src/hooks/use-pathname";
import { useWindowScroll } from "src/hooks/use-window-scroll";
import { paths } from "src/paths";

import { LanguageSwitch } from "src/layouts/dashboard/language-switch";
import { BrandMark } from "./brand-mark";
import { itemsTopNav } from "./items";
import { TopNavItem } from "./top-nav-item";

const TOP_NAV_HEIGHT = 64;
const NAVY = "#071525";

const navButtonBase = {
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: "0.01em",
  borderRadius: 1.5,
  px: { xs: 1.5, md: 2 },
  minHeight: 36,
  boxShadow: "none",
  whiteSpace: "nowrap" as const,
};

interface TopNavProps {
  onMobileNavOpen?: () => void;
}

export const TopNav: FC<TopNavProps> = (props) => {
  const { t } = useTranslation();
  const { onMobileNavOpen } = props;
  const pathname = usePathname();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const [elevate, setElevate] = useState<boolean>(false);

  const handleWindowScroll = useCallback((): void => {
    setElevate(window.scrollY > 8);
  }, []);

  useWindowScroll({
    handler: handleWindowScroll,
    delay: 80,
  });

  return (
    <Box
      component="header"
      sx={{
        left: 0,
        position: "fixed",
        right: 0,
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        borderBottom: (theme) =>
          elevate
            ? `1px solid ${theme.palette.divider}`
            : "1px solid transparent",
        backgroundColor: (theme) =>
          elevate ? theme.palette.background.paper : "transparent",
        transition: (theme) =>
          theme.transitions.create(["background-color", "border-color"], {
            duration: 160,
          }),
      }}
    >
      <Container maxWidth="lg">
        <Stack direction="row" spacing={2} sx={{ height: TOP_NAV_HEIGHT }}>
          <Stack
            alignItems="center"
            direction="row"
            spacing={1}
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            {!mdUp && (
              <IconButton onClick={onMobileNavOpen} aria-label="Open menu">
                <SvgIcon fontSize="small">
                  <Menu01Icon />
                </SvgIcon>
              </IconButton>
            )}
            <Box
              component={RouterLink}
              href={paths.index}
              sx={{ textDecoration: "none", minWidth: 0 }}
            >
              <BrandMark />
            </Box>
          </Stack>
          {mdUp && (
            <Box component="nav" sx={{ height: "100%" }}>
              <Stack
                component="ul"
                alignItems="center"
                justifyContent="center"
                direction="row"
                spacing={0.5}
                sx={{
                  height: "100%",
                  listStyle: "none",
                  m: 0,
                  p: 0,
                }}
              >
                {itemsTopNav.map((item) => {
                  const checkPath = !!(item.path && pathname);
                  const partialMatch = checkPath
                    ? pathname.includes(item.path!)
                    : false;
                  const exactMatch = checkPath ? pathname === item.path : false;
                  const active = item.popover ? partialMatch : exactMatch;

                  return (
                    <TopNavItem
                      active={active}
                      external={item.external}
                      key={item.title}
                      path={item.path}
                      popover={item.popover}
                      title={item.title}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{ flexGrow: { xs: 0, md: 1 }, flexShrink: 0 }}
          >
            <LanguageSwitch />
            <Button
              component="a"
              href={paths.auth.firebase.login}
              variant="outlined"
              sx={{
                ...navButtonBase,
                color: NAVY,
                borderColor: "rgba(7, 21, 37, 0.2)",
                backgroundColor: "transparent",
                "&:hover": {
                  borderColor: NAVY,
                  backgroundColor: "rgba(7, 21, 37, 0.04)",
                  boxShadow: "none",
                },
              }}
            >
              {t("Login")}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

TopNav.propTypes = {
  onMobileNavOpen: PropTypes.func,
};
